import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { servicesApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { parseAsset, assetKeys, type Asset } from './entity';

/**
 * Asset mutations — create folder, move, delete.
 *
 * File uploads use multipart/form-data and are handled separately in
 * `upload-asset.ts` since ts-rest doesn't support FormData bodies.
 *
 * **No optimistic flows.** The server validates naming, permissions, and
 * folder structure. Easier to write the server's truth into the cache
 * on success than to predict and roll back.
 */

/* ------------------------------------------------------------------------- */
/* createAsset (folder creation)                                              */
/* ------------------------------------------------------------------------- */

export interface CreateAssetInput {
  workspaceId: number;
  name: string;
  is_dir: boolean;
  parent_id?: string | null;
  path?: string;
  size?: number;
  file_type?: string;
}

/** @public */
export async function createAsset(input: CreateAssetInput): Promise<Asset> {
  const res = await servicesApi.createAsset({
    params: { environment_id: input.workspaceId },
    body: {
      asset: {
        name: input.name,
        is_dir: input.is_dir,
        parent_id: input.parent_id,
        path: input.path,
        size: input.size,
      },
      file_type: input.file_type,
    },
  });
  return parseAsset(res.asset);
}

type UseCreateAssetVariables = Omit<CreateAssetInput, 'workspaceId'>;

interface UseCreateAssetOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<Asset, UseCreateAssetVariables>;
}

/** @public */
export const useCreateAsset = ({ workspaceId: overrideWorkspaceId, mutationConfig }: UseCreateAssetOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseCreateAssetVariables) => createAsset({ workspaceId, ...variables }),
    onSuccess: (created, ...callbackArgs) => {
      qc.setQueryData(assetKeys.detail(workspaceId, created.id), created);
      void qc.invalidateQueries({ queryKey: assetKeys.lists(workspaceId) });
      onSuccess?.(created, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* moveAssets                                                                  */
/* ------------------------------------------------------------------------- */

export interface MoveAssetsInput {
  workspaceId: number;
  assets: Array<{
    asset: {
      id: string;
      name: string;
      is_dir: boolean;
      parent_id: string | null;
      path: string;
      size: number;
    };
  }>;
}

/** @public */
export async function moveAssets(input: MoveAssetsInput): Promise<void> {
  await servicesApi.moveAssets({
    params: { environment_id: input.workspaceId },
    body: {
      assets: input.assets.map(({ asset }) => ({ asset })),
    },
  });
}

type UseMoveAssetsVariables = Omit<MoveAssetsInput, 'workspaceId'>;

export function removeMovedAssetDetails(
  qc: QueryClient,
  workspaceId: number,
  movedAssets: UseMoveAssetsVariables['assets'],
): void {
  for (const { asset: movedAsset } of movedAssets) {
    qc.removeQueries({ queryKey: assetKeys.detail(workspaceId, movedAsset.id) });
  }
}

interface UseMoveAssetsOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, UseMoveAssetsVariables>;
}

/** @public */
export const useMoveAssets = ({ workspaceId: overrideWorkspaceId, mutationConfig }: UseMoveAssetsOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseMoveAssetsVariables) => moveAssets({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      removeMovedAssetDetails(qc, workspaceId, variables.assets);
      void qc.invalidateQueries({ queryKey: assetKeys.lists(workspaceId) });
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* deleteAssets                                                                */
/* ------------------------------------------------------------------------- */

export interface DeleteAssetsInput {
  workspaceId: number;
  asset_ids: string[];
}

/** @public */
export async function deleteAssets(input: DeleteAssetsInput): Promise<void> {
  // The Go API expects integer asset_ids for delete (unlike move which uses strings)
  await servicesApi.deleteAssets({
    params: { environment_id: input.workspaceId },
    body: { asset_ids: input.asset_ids.map(Number) },
  });
}

type UseDeleteAssetsVariables = Omit<DeleteAssetsInput, 'workspaceId'>;

interface UseDeleteAssetsOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, UseDeleteAssetsVariables>;
}

/** @public */
export const useDeleteAssets = ({ workspaceId: overrideWorkspaceId, mutationConfig }: UseDeleteAssetsOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseDeleteAssetsVariables) => deleteAssets({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      for (const id of variables.asset_ids) {
        qc.removeQueries({ queryKey: assetKeys.detail(workspaceId, id) });
      }
      void qc.invalidateQueries({ queryKey: assetKeys.lists(workspaceId) });
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};
